import { Transform } from 'ff-canvas';

/**
 * Describes scene space and design space. Used by the rendering pipeline to
 * position object coordinates into the scene's design area (using a global
 * translation/scale).
 */
export type Spaces = {

    // Scene space: outer box of the scene, including padding and product mockup.

    sceneWidth: number;
    sceneHeight: number;

    // Design area: a rectangle within scene space that will hold the design.

    designAreaX: number;
    designAreaY: number;
    designAreaWidth: number;
    designAreaHeight: number;

    // Design space: outer box of the design.

    designWidth: number;
    designHeight: number;
};

/**
 * Creates a global ff-canvas transform that applies the translation/scale
 * mentioned at {@link Spaces}. The design is scaled in its container
 * similarly to `background-size: contain` in CSS (centered).
 */
export const createTransform = (
    canvasPxWidth: number, canvasPxHeight: number,
    spaces: Spaces
) => {
    const {
        sceneWidth, sceneHeight,
        designAreaX, designAreaY,
        designAreaWidth, designAreaHeight,
        designWidth, designHeight
    } = spaces;

    const designScale = calculateDesignScale(
        designAreaWidth, designAreaHeight,
        designWidth, designHeight);

    const sceneXAxisToPx = canvasPxWidth / sceneWidth;
    const sceneYAxisToPx = canvasPxHeight / sceneHeight;

    const scale = {
        x: sceneXAxisToPx * designScale,
        y: sceneYAxisToPx * designScale
    };

    const removeFfCanvasOriginX = Math.floor(-0.5 * canvasPxWidth);
    const removeFfCanvasOriginY = Math.floor(-0.5 * canvasPxHeight);

    const sceneCenterX = 0.5 * sceneWidth;
    const sceneCenterY = 0.5 * sceneHeight;

    const translate = {
        x: removeFfCanvasOriginX + (sceneCenterX + designAreaX) / designScale,
        y: removeFfCanvasOriginY + (sceneCenterY + designAreaY) / designScale
    };

    return new Transform(scale, translate);
};

const calculateDesignScale = (
    designAreaWidth: number, designAreaHeight: number,
    designWidth: number, designHeight: number
) => {
    const sceneRatio = designAreaHeight / designAreaWidth;
    const designRatio = designHeight / designWidth;

    const widthScale = designAreaWidth / designWidth;
    const heightScale = designAreaHeight / designHeight;

    return sceneRatio >= designRatio
        ? widthScale
        : heightScale;
};

/**
 * The numbers needed to undo the global {@link Spaces} transform. This
 * "undo-viewbox" data can be applied to the coordinates of background
 * paintables (e.g. product mockup) so that they cover the entire scene,
 * not just the design area.
 *
 * (Note: this workaround is probably not suited to scenarios where you need
 * to scale more than just x/y/w/h. Properties like `fontSize`, `strokeSize`
 * and `shadowBlur` may not be so easy to correct.)
 */
export type UndoViewbox = {
    scaling: number;
    offsetX: number;
    offsetY: number;
};

/**
 * @see UndoViewbox
 */
export const createUndoViewbox = (spaces: Spaces): UndoViewbox => {

    const {
        designAreaX, designAreaY,
        designAreaWidth, designAreaHeight,
        designWidth, designHeight
    } = spaces;

    const designScale = calculateDesignScale(
        designAreaWidth, designAreaHeight,
        designWidth, designHeight);

    return {
        scaling: 1 / designScale,
        offsetX: -designAreaX / designScale,
        offsetY: -designAreaY / designScale
    };
};